// DateRangePickerComponent.js
import React from 'react';
import { DateRangePicker } from 'rsuite';
import subDays from 'date-fns/subDays';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import addDays from 'date-fns/addDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addMonths from 'date-fns/addMonths';
import 'rsuite/DateRangePicker/styles/index.css';

const formatDate = (date) => {
  const yyyy = date?.getFullYear();
  const mm = String(date?.getMonth() + 1).padStart(2, '0');
  const dd = String(date?.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getFinancialQuarterRange = (date) => {
  const month = date.getMonth();
  const year = date.getFullYear();
  const fyStartYear = month < 3 ? year - 1 : year;

  if (month >= 3 && month <= 5) {
    return [new Date(fyStartYear, 3, 1), new Date(fyStartYear, 5, 30)];
  } else if (month >= 6 && month <= 8) {
    return [new Date(fyStartYear, 6, 1), new Date(fyStartYear, 8, 30)];
  } else if (month >= 9 && month <= 11) {
    return [new Date(fyStartYear, 9, 1), new Date(fyStartYear, 11, 31)];
  } else {
    return [new Date(fyStartYear + 1, 0, 1), new Date(fyStartYear + 1, 2, 31)];
  }
};

const getPreviousFinancialYearRange = (date) => {
  const year = date.getMonth() < 3 ? date?.getFullYear() - 1 : date?.getFullYear();
  return [new Date(year - 1, 3, 1), new Date(year, 2, 31)];
};

const getFinancialYearRange = (date) => {
  const year = date.getMonth() < 3 ? date?.getFullYear() - 1 : date?.getFullYear();
  return [new Date(year, 3, 1), new Date(year + 1, 2, 31)];
};

const getQuarterRange = (quarter, year = new Date().getFullYear()) => {
  const fyStartYear = new Date().getMonth() >= 3 ? year : year - 1;
  const quarterMap = {
    Q1: [new Date(fyStartYear, 3, 1), new Date(fyStartYear, 5, 30)],
    Q2: [new Date(fyStartYear, 6, 1), new Date(fyStartYear, 8, 30)],
    Q3: [new Date(fyStartYear, 9, 1), new Date(fyStartYear, 11, 31)],
    Q4: [new Date(fyStartYear + 1, 0, 1), new Date(fyStartYear + 1, 2, 31)],
  };
  return quarterMap[quarter];
};

const predefinedRanges = [
  {
    label: 'Last year',
    closeOverlay: false,
    value: getPreviousFinancialYearRange(new Date()),
    placement: 'left'
  },
  {
    label: 'Last month',
    closeOverlay: false,
    value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
    placement: 'left'
  },
  {
    label: 'Last week',
    closeOverlay: false,
    value: () => {
      const today = new Date();
      return [
        addDays(startOfWeek(today, { weekStartsOn: 0 }), -7),
        addDays(endOfWeek(today, { weekStartsOn: 0 }), -7)
      ];
    },
    placement: 'left'
  },
  {
    label: 'Yesterday',
    closeOverlay: false,
    value: [addDays(new Date(), -1), addDays(new Date(), -1)],
    placement: 'left'
  },
  {
    label: 'Today',
    closeOverlay: false,
    value: [new Date(), new Date()],
    placement: 'left'
  },
  {
    label: 'This week',
    closeOverlay: false,
    value: [startOfWeek(new Date(), { weekStartsOn: 0 }), endOfWeek(new Date(), { weekStartsOn: 0 })],
    placement: 'left'
  },
  {
    label: 'This month',
    closeOverlay: false,
    value: [startOfMonth(new Date()), new Date()],
    placement: 'left'
  },
  {
    label: 'Quarter 1',
    closeOverlay: false,
    value: getQuarterRange('Q1'),
    placement: 'left'
  },
  {
    label: 'Quarter 2',
    closeOverlay: false,
    value: getQuarterRange('Q2'),
    placement: 'left'
  },
  {
    label: 'Quarter 3',
    closeOverlay: false,
    value: getQuarterRange('Q3'),
    placement: 'left'
  },
  {
    label: 'Quarter 4',
    closeOverlay: false,
    value: getQuarterRange('Q4'),
    placement: 'left'
  },
  {
    label: 'This year',
    closeOverlay: false,
    value: getFinancialYearRange(new Date()),
    placement: 'left'
  }
];

const DateRangePickerComponent = ({ value, onChange, showOneCalendar }) => {
  const handleChange = (range) => {
    if (!range) return onChange(null);

    const [start, end] = range;

    // Send only formatted strings like '2025-05-01'
    onChange([formatDate(start), formatDate(end)]);
  };

  return (
    <DateRangePicker
      ranges={predefinedRanges}
      value={
        Array.isArray(value) && value.length === 2 && value[0] && value[1]
          ? value.map(d => new Date(d))
          : null
      }
      onChange={handleChange}
      placeholder="Select Date Range"
      style={{ width: '100%', borderColor: 'grey' }}
      cleanable={false}
      showOneCalendar = {true}
      
    />
  );
};

export default DateRangePickerComponent;
